import { useEffect, useState, useMemo, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Filter, Plus, X, Save, Star, Share2, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

const OPS = {
  text: ["contains", "is", "is not", "is empty", "is not empty"],
  number: ["equals", "greater than", "less than", "is empty", "is not empty"],
  select: ["is", "is not", "is empty", "is not empty"],
  date: ["before", "after", "on", "is empty", "is not empty"],
};

export function matchConditions(row, conditions, match, fields) {
  const valid = (conditions || []).filter((c) => c.field && c.op);
  if (!valid.length) return true;
  const typeOf = (k) => fields.find((f) => f.key === k)?.type || "text";
  const one = (c) => {
    const raw = row[c.field];
    if (c.op === "is empty") return raw == null || raw === "";
    if (c.op === "is not empty") return !(raw == null || raw === "");
    const type = typeOf(c.field);
    if (type === "number") {
      const a = Number(raw), b = Number(c.value);
      if (c.op === "equals") return a === b;
      if (c.op === "greater than") return a > b;
      if (c.op === "less than") return a < b;
    }
    if (type === "date") {
      const rv = raw ? new Date(raw) : null; if (!rv || !c.value) return false;
      const cv = new Date(c.value);
      if (c.op === "before") return rv < cv;
      if (c.op === "after") return rv > cv;
      if (c.op === "on") return rv.toISOString().slice(0, 10) === c.value;
    }
    const v = (raw ?? "").toString().toLowerCase();
    const val = (c.value ?? "").toString().toLowerCase();
    if (c.op === "contains") return v.includes(val);
    if (c.op === "is") return v === val;
    if (c.op === "is not") return v !== val;
    return true;
  };
  const res = valid.map(one);
  return match === "OR" ? res.some(Boolean) : res.every(Boolean);
}

export default function AdvancedFilters({ entity, fields, rows, onFiltered, persist }) {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "admin"].includes(user.role);
  const stored = persist ? JSON.parse(sessionStorage.getItem(`af:${entity}`) || "null") : null;
  const [conditions, setConditions] = useState(stored?.conditions || []);
  const [match, setMatch] = useState(stored?.match || "AND");
  useEffect(() => { if (persist) sessionStorage.setItem(`af:${entity}`, JSON.stringify({ conditions, match })); }, [conditions, match, persist, entity]);
  const [show, setShow] = useState(false);
  const [saved, setSaved] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [shareFor, setShareFor] = useState(null);

  const loadSaved = useCallback(() => {
    api.get(`/saved-filters?entity=${entity}`).then(({ data }) => setSaved(data)).catch(() => {});
  }, [entity]);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const opsFor = (key) => OPS[fields.find((f) => f.key === key)?.type || "text"];
  const filtered = useMemo(() => (rows === null ? null : rows.filter((r) => matchConditions(r, conditions, match, fields))), [rows, conditions, match, fields]);
  useEffect(() => { onFiltered(filtered); }, [filtered]); // eslint-disable-line

  const addCondition = () => setConditions([...conditions, { field: fields[0].key, op: opsFor(fields[0].key)[0], value: "" }]);
  const setCond = (i, patch) => setConditions(conditions.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  const removeCond = (i) => setConditions(conditions.filter((_, idx) => idx !== i));
  const clearAll = () => { setConditions([]); setActiveId(""); };

  const applySaved = (f) => { setConditions(f.conditions || []); setMatch(f.match || "AND"); setActiveId(f.id); setShow(true); };

  const doSave = async () => {
    if (!saveName.trim()) { toast.error("Enter a name"); return; }
    try {
      const own = saved.find((f) => f.id === activeId && f.owner_id === user.id);
      if (own) await api.patch(`/saved-filters/${own.id}`, { name: saveName, conditions, match });
      else { const { data } = await api.post("/saved-filters", { name: saveName, entity, conditions, match }); setActiveId(data.id); }
      toast.success(`Saved filter "${saveName}"`); setSaveOpen(false); loadSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    try { await api.delete(`/saved-filters/${id}`); toast.success("Filter deleted"); if (activeId === id) clearAll(); loadSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleShare = async (f, shared) => {
    try { await api.post(`/saved-filters/${f.id}/share`, { shared_all: shared }); toast.success(shared ? "Shared with all counselors" : "Sharing turned off"); setShareFor(null); loadSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const mine = saved.filter((f) => f.owner_id === user.id);
  const sharedWithMe = saved.filter((f) => f.owner_id !== user.id);

  return (
    <div className="mb-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" onClick={() => setShow((s) => !s)} data-testid={`${entity}-adv-filter-toggle`}>
          <Filter className="w-4 h-4 mr-1" /> Advanced Filters{conditions.filter((c) => c.field && c.op).length ? ` (${conditions.filter((c) => c.field && c.op).length})` : ""}
        </Button>
        {/* saved filter chips */}
        {mine.map((f) => (
          <span key={f.id} className={`inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-xs border ${activeId === f.id ? "bg-indigo-50 border-indigo-300 text-[#4F46E5]" : "bg-white border-slate-200 text-slate-600"}`}>
            <button onClick={() => applySaved(f)} data-testid={`saved-filter-${f.id}`} className="flex items-center gap-1"><Star className="w-3 h-3" /> {f.name}{f.shared_all && <Users className="w-3 h-3 text-emerald-500" title="Shared" />}</button>
            {isAdmin && <button onClick={() => setShareFor(f)} className="w-4 h-4 rounded-full hover:bg-slate-200 flex items-center justify-center" title="Share" data-testid={`share-filter-${f.id}`}><Share2 className="w-3 h-3" /></button>}
            <button onClick={() => del(f.id)} className="w-4 h-4 rounded-full hover:bg-rose-100 flex items-center justify-center" title="Delete"><X className="w-3 h-3" /></button>
          </span>
        ))}
        {sharedWithMe.map((f) => (
          <span key={f.id} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border ${activeId === f.id ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-emerald-50/50 border-emerald-200 text-emerald-700"}`}>
            <button onClick={() => applySaved(f)} data-testid={`shared-filter-${f.id}`} className="flex items-center gap-1"><Users className="w-3 h-3" /> {f.name}<span className="text-[10px] text-emerald-500">· {f.owner_name}</span></button>
            {isAdmin && <button onClick={() => del(f.id)} className="w-4 h-4 rounded-full hover:bg-rose-100 flex items-center justify-center" title="Delete"><X className="w-3 h-3" /></button>}
          </span>
        ))}
      </div>

      {show && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 mt-3 shadow-sm" data-testid={`${entity}-filter-builder`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#312E81]"><Filter className="w-4 h-4" /> Custom Filter Conditions</div>
            <div className="flex items-center gap-2 text-xs"><span className="text-slate-500">Match</span>
              <Select value={match} onValueChange={setMatch}>
                <SelectTrigger className="h-8 w-28 bg-white" data-testid="filter-match"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="AND">ALL (AND)</SelectItem><SelectItem value="OR">ANY (OR)</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            {conditions.map((c, i) => {
              const fld = fields.find((f) => f.key === c.field) || fields[0];
              const showVal = !["is empty", "is not empty"].includes(c.op);
              return (
                <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`filter-condition-${i}`}>
                  <Select value={c.field} onValueChange={(v) => setCond(i, { field: v, op: opsFor(v)[0], value: "" })}>
                    <SelectTrigger className="h-9 w-48 bg-white" data-testid={`filter-field-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white max-h-72">{fields.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={c.op} onValueChange={(v) => setCond(i, { op: v })}>
                    <SelectTrigger className="h-9 w-40 bg-white" data-testid={`filter-op-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{opsFor(c.field).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                  {showVal && (fld.type === "select"
                    ? <Select value={c.value} onValueChange={(v) => setCond(i, { value: v })}><SelectTrigger className="h-9 w-48 bg-white" data-testid={`filter-value-${i}`}><SelectValue placeholder="Value" /></SelectTrigger><SelectContent className="bg-white max-h-72">{(fld.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
                    : <Input value={c.value || ""} onChange={(e) => setCond(i, { value: e.target.value })} placeholder="Value" type={fld.type === "number" ? "number" : fld.type === "date" ? "date" : "text"} className="h-9 w-48 bg-white" data-testid={`filter-value-${i}`} />)}
                  <button onClick={() => removeCond(i)} className="w-8 h-8 rounded-lg hover:bg-rose-50 text-rose-500 flex items-center justify-center"><X className="w-4 h-4" /></button>
                </div>
              );
            })}
            {conditions.length === 0 && <div className="text-xs text-slate-400">No conditions yet. Add one to start filtering.</div>}
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <Button size="sm" variant="outline" onClick={addCondition} data-testid="add-condition-btn"><Plus className="w-4 h-4 mr-1" /> Add a new condition</Button>
            <Button size="sm" className="bg-[#4F46E5] hover:bg-[#4338CA]" onClick={() => { setSaveName(saved.find((f) => f.id === activeId)?.name || ""); setSaveOpen(true); }} data-testid="save-view-btn"><Save className="w-4 h-4 mr-1" /> Save filter</Button>
            <Button size="sm" variant="ghost" onClick={clearAll} data-testid="clear-filters-btn">Clear all</Button>
            <span className="text-xs text-slate-400 ml-auto">{filtered?.length ?? 0} of {rows?.length ?? 0}</span>
          </div>
        </div>
      )}

      {/* Save dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Save Filter</DialogTitle></DialogHeader>
          <div><Label>Filter Name</Label><Input value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="e.g. Hot MBBS Bangalore" data-testid="save-filter-name" /></div>
          <p className="text-xs text-slate-400">Saved to your account. Admins can share it with all counselors from the filter chip.</p>
          <DialogFooter><Button onClick={doSave} className="bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="confirm-save-filter">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share dialog (admin) */}
      <Dialog open={!!shareFor} onOpenChange={(o) => !o && setShareFor(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Share2 className="w-5 h-5 text-[#4F46E5]" /> Share Filter</DialogTitle></DialogHeader>
          {shareFor && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Share <span className="font-semibold">{shareFor.name}</span> with all counselors. It will appear in their filter list.</p>
              <div className="flex gap-2">
                <Button onClick={() => toggleShare(shareFor, true)} className="bg-emerald-600 hover:bg-emerald-700 flex-1" data-testid="confirm-share-all"><Users className="w-4 h-4 mr-1" /> Share with all counselors</Button>
                {shareFor.shared_all && <Button variant="outline" onClick={() => toggleShare(shareFor, false)} data-testid="unshare-btn">Stop sharing</Button>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
